/**
 * Library of ready-to-use overlay templates.
 *
 * Each entry produces a "template" — the same partial as `OverlayLayer` minus
 * the `uid` and `fromSec`/positional bits that the Composer will fill in
 * (using the playhead and an optional drop position). This keeps the catalog
 * declarative and lets us drop the same element multiple times without
 * accidentally sharing IDs.
 *
 * Convention: every template produces an overlay that's centered (xPct=50,
 * yPct=50) and lives on track 10. The drop-handler overrides xPct/yPct based
 * on where in the preview the user dropped it.
 */

import type {
  BadgeOverlayLayer,
  IconOverlayLayer,
  OverlayLayer,
  ShapeOverlayLayer,
  TextOverlayLayer,
} from "../../api";

export type ElementTemplate = Omit<OverlayLayer, "uid" | "fromSec">;

export type ElementCategoryId = "text" | "shape" | "icon" | "badge" | "image";

export type ElementEntry = {
  /** Stable id used as drag payload + react key. */
  id: string;
  /** Short, user-facing label shown under the card. */
  label: string;
  /** Optional one-line hint shown in the tooltip. */
  hint?: string;
  /** Visual preview chip — emoji or short text. */
  preview: string;
  /** Material category the entry belongs to. */
  category: ElementCategoryId;
  /** Builder so each drop produces a fresh, mutable copy. */
  build: () => ElementTemplate;
};

const baseDefaults = {
  durSec: 3,
  track: 10,
  xPct: 50,
  yPct: 50,
  scale: 1,
  rotate: 0,
  opacity: 1,
};

/* ============================================================ *
 *  Text presets                                                  *
 * ============================================================ */

const text = (
  id: string,
  label: string,
  preview: string,
  build: () => Omit<TextOverlayLayer, "uid" | "fromSec">,
  hint?: string,
): ElementEntry => ({ id, label, preview, hint, category: "text", build });

export const TEXT_PRESETS: ElementEntry[] = [
  text(
    "text-hook-xl",
    "Hook XXL",
    "HOOK!",
    () => ({
      ...baseDefaults,
      kind: "text",
      text: "STOP\nSCROLLING",
      fontSize: 140,
      weight: 900,
      color: "primary",
      align: "center",
      shadow: true,
      letterSpacing: -0.02,
      yPct: 32,
    }),
    "Riesige fette Hook für die ersten 1.5s",
  ),
  text(
    "text-headline",
    "Headline",
    "Headline",
    () => ({
      ...baseDefaults,
      kind: "text",
      text: "Deine Headline",
      fontSize: 96,
      weight: 800,
      color: "white",
      align: "center",
      shadow: true,
      yPct: 38,
    }),
  ),
  text(
    "text-eyebrow",
    "Eyebrow",
    "EYEBROW",
    () => ({
      ...baseDefaults,
      kind: "text",
      text: "PROBLEM",
      fontSize: 38,
      weight: 700,
      color: "accent",
      align: "center",
      letterSpacing: 0.12,
      shadow: false,
      yPct: 26,
    }),
    "Kleine Auge-darüber-Zeile, große Wirkung",
  ),
  text(
    "text-cta",
    "CTA",
    "→ JETZT",
    () => ({
      ...baseDefaults,
      kind: "text",
      text: "→ JETZT TESTEN",
      fontSize: 64,
      weight: 800,
      color: "primary",
      align: "center",
      shadow: true,
      yPct: 78,
    }),
    "Call-to-Action-Zeile am unteren Rand",
  ),
  text(
    "text-quote",
    "Zitat",
    "„…“",
    () => ({
      ...baseDefaults,
      kind: "text",
      text: "„Das hat alles verändert.“",
      fontSize: 60,
      weight: 600,
      color: "white",
      align: "center",
      shadow: true,
      maxWidthPct: 80,
    }),
  ),
  text(
    "text-caption",
    "Caption",
    "caption",
    () => ({
      ...baseDefaults,
      kind: "text",
      text: "Untertitel",
      fontSize: 36,
      weight: 600,
      color: "white",
      align: "center",
      shadow: true,
      yPct: 88,
    }),
    "Kleine Bildunterschrift / Quelle",
  ),
  text(
    "text-lower-third",
    "Lower Third",
    "Name · Titel",
    () => ({
      ...baseDefaults,
      kind: "text",
      text: "Maria Schmidt · CMO",
      fontSize: 44,
      weight: 700,
      color: "white",
      align: "left",
      shadow: true,
      xPct: 22,
      yPct: 80,
      maxWidthPct: 70,
    }),
  ),
  text(
    "text-stat",
    "Stat-Wert",
    "+184%",
    () => ({
      ...baseDefaults,
      kind: "text",
      text: "+184%",
      fontSize: 180,
      weight: 900,
      color: "accent",
      align: "center",
      shadow: true,
      letterSpacing: -0.04,
    }),
    "Riesiger Zahlenwert mittig",
  ),
];

/* ============================================================ *
 *  Shapes                                                        *
 * ============================================================ */

const shape = (
  id: string,
  label: string,
  preview: string,
  build: () => Omit<ShapeOverlayLayer, "uid" | "fromSec">,
  hint?: string,
): ElementEntry => ({ id, label, preview, hint, category: "shape", build });

export const SHAPE_PRESETS: ElementEntry[] = [
  shape("shape-pill", "Pille", "⬭", () => ({
    ...baseDefaults,
    kind: "shape",
    shape: "pill",
    widthPct: 30,
    heightPct: 8,
    fill: "primary",
  })),
  shape("shape-rect", "Rechteck", "▭", () => ({
    ...baseDefaults,
    kind: "shape",
    shape: "rect",
    widthPct: 40,
    heightPct: 10,
    fill: "primary",
    radius: 16,
  })),
  shape("shape-banner", "Banner", "▬", () => ({
    ...baseDefaults,
    kind: "shape",
    shape: "rect",
    widthPct: 110,
    heightPct: 14,
    fill: "primary",
    radius: 0,
    yPct: 86,
  })),
  shape("shape-circle", "Kreis", "●", () => ({
    ...baseDefaults,
    kind: "shape",
    shape: "circle",
    widthPct: 18,
    heightPct: 32,
    fill: "accent",
  })),
  shape("shape-frame", "Rahmen", "◻", () => ({
    ...baseDefaults,
    kind: "shape",
    shape: "rect",
    widthPct: 80,
    heightPct: 60,
    fill: "transparent",
    stroke: "white",
    strokeWidth: 6,
    radius: 24,
  })),
  shape("shape-divider", "Trenner", "—", () => ({
    ...baseDefaults,
    kind: "shape",
    shape: "rect",
    widthPct: 14,
    heightPct: 0.6,
    fill: "accent",
    radius: 4,
  })),
  shape("shape-star", "Stern", "★", () => ({
    ...baseDefaults,
    kind: "shape",
    shape: "star",
    widthPct: 16,
    heightPct: 28,
    fill: "accent",
  })),
  shape("shape-arrow", "Pfeil", "➜", () => ({
    ...baseDefaults,
    kind: "shape",
    shape: "arrow",
    widthPct: 22,
    heightPct: 12,
    fill: "primary",
  })),
  shape("shape-triangle", "Dreieck", "▲", () => ({
    ...baseDefaults,
    kind: "shape",
    shape: "triangle",
    widthPct: 18,
    heightPct: 22,
    fill: "secondary",
  })),
  shape("shape-highlight", "Highlight", "▮", () => ({
    ...baseDefaults,
    kind: "shape",
    shape: "rect",
    widthPct: 50,
    heightPct: 16,
    fill: "accent",
    radius: 6,
    opacity: 0.42,
  })),
];

/* ============================================================ *
 *  Icons                                                         *
 * ============================================================ */

const icon = (
  id: string,
  label: string,
  preview: string,
  build: () => Omit<IconOverlayLayer, "uid" | "fromSec">,
): ElementEntry => ({ id, label, preview, category: "icon", build });

const ICON_NAMES: { id: string; glyph: string; label: string }[] = [
  { id: "star", glyph: "★", label: "Stern" },
  { id: "heart", glyph: "♥", label: "Herz" },
  { id: "check", glyph: "✓", label: "Check" },
  { id: "x", glyph: "✕", label: "X" },
  { id: "bolt", glyph: "⚡", label: "Bolt" },
  { id: "sparkles", glyph: "✦", label: "Sparkles" },
  { id: "arrow-right", glyph: "→", label: "Pfeil rechts" },
  { id: "arrow-up", glyph: "↑", label: "Pfeil hoch" },
  { id: "flame", glyph: "🔥", label: "Flame" },
  { id: "crown", glyph: "♛", label: "Krone" },
  { id: "pin", glyph: "📍", label: "Pin" },
  { id: "play", glyph: "▶", label: "Play" },
  { id: "shoppingbag", glyph: "🛍", label: "Tasche" },
  { id: "globe", glyph: "🌐", label: "Globus" },
  { id: "euro", glyph: "€", label: "Euro" },
];

export const ICON_PRESETS: ElementEntry[] = ICON_NAMES.map((i) =>
  icon(`icon-${i.id}`, i.label, i.glyph, () => ({
    ...baseDefaults,
    kind: "icon",
    icon: i.id,
    sizePct: 14,
    color: "accent",
  })),
);

/* ============================================================ *
 *  Badges                                                        *
 * ============================================================ */

const badge = (
  id: string,
  label: string,
  preview: string,
  build: () => Omit<BadgeOverlayLayer, "uid" | "fromSec">,
  hint?: string,
): ElementEntry => ({ id, label, preview, hint, category: "badge", build });

export const BADGE_PRESETS: ElementEntry[] = [
  badge("badge-live", "LIVE", "● LIVE", () => ({
    ...baseDefaults,
    kind: "badge",
    text: "LIVE",
    fontSize: 36,
    fill: "#ef4444",
    textColor: "white",
    yPct: 12,
    xPct: 16,
  })),
  badge("badge-new", "NEU", "★ NEU", () => ({
    ...baseDefaults,
    kind: "badge",
    text: "NEU",
    icon: "sparkles",
    fontSize: 32,
    fill: "accent",
    textColor: "black",
  })),
  badge("badge-sale", "SALE", "% SALE", () => ({
    ...baseDefaults,
    kind: "badge",
    text: "−40%",
    fontSize: 56,
    fill: "secondary",
    textColor: "white",
    rotate: -8,
  })),
  badge("badge-cta", "CTA-Pille", "→ Mehr", () => ({
    ...baseDefaults,
    kind: "badge",
    text: "MEHR ERFAHREN",
    icon: "arrow-right",
    fontSize: 28,
    fill: "primary",
    textColor: "white",
    yPct: 80,
  })),
  badge("badge-tip", "Tipp", "💡 Tipp", () => ({
    ...baseDefaults,
    kind: "badge",
    text: "PRO TIPP",
    icon: "bolt",
    fontSize: 26,
    fill: "white",
    textColor: "black",
  })),
  badge("badge-shop", "Shop", "🛍 SHOP", () => ({
    ...baseDefaults,
    kind: "badge",
    text: "JETZT SHOPPEN",
    icon: "shoppingbag",
    fontSize: 28,
    fill: "primary",
    textColor: "white",
    yPct: 88,
  })),
  badge("badge-rating", "5★", "★ 5.0", () => ({
    ...baseDefaults,
    kind: "badge",
    text: "5.0 ★★★★★",
    fontSize: 32,
    fill: "white",
    textColor: "black",
  })),
  badge("badge-flame", "Trending", "🔥 Trend", () => ({
    ...baseDefaults,
    kind: "badge",
    text: "TRENDING",
    icon: "flame",
    fontSize: 28,
    fill: "secondary",
    textColor: "white",
  })),
];

/* ============================================================ *
 *  Images / Logos — dynamic                                       *
 *  ------------------------------------------------------------ *
 *  Image entries aren't enumerated statically. The library panel  *
 *  pulls them at runtime (brand logo + user uploads). Drag&drop   *
 *  uses the `image:<url>` id prefix so the central               *
 *  `findElementById` resolver can synthesize a fresh template     *
 *  per-drop without registering anything in the static catalog.   *
 * ============================================================ */

/** Build an ad-hoc image overlay template from a URL. */
export function imageOverlayTemplate(
  src: string,
  opts?: { widthPct?: number; heightPct?: number; fit?: "cover" | "contain" },
): ElementTemplate {
  return {
    ...baseDefaults,
    kind: "image",
    src,
    widthPct: opts?.widthPct ?? 30,
    heightPct: opts?.heightPct ?? 30,
    fit: opts?.fit ?? "contain",
  };
}

/** Prefix used in DragPayload.elementId for ad-hoc image entries. */
export const IMAGE_DRAG_PREFIX = "image:";

/* ============================================================ *
 *  Public catalog                                                *
 * ============================================================ */

export const ELEMENT_CATEGORIES: { id: ElementCategoryId; label: string; glyph: string; entries: ElementEntry[] }[] = [
  { id: "text", label: "Text", glyph: "T", entries: TEXT_PRESETS },
  { id: "shape", label: "Formen", glyph: "▭", entries: SHAPE_PRESETS },
  { id: "icon", label: "Icons", glyph: "✦", entries: ICON_PRESETS },
  { id: "badge", label: "Badges", glyph: "●", entries: BADGE_PRESETS },
  { id: "image", label: "Bilder", glyph: "🖼", entries: [] },
];

/** Drag&drop payload shape (JSON-serialized in dataTransfer). */
export const DRAG_MIME = "application/x-jusa-element";

export type DragPayload = {
  elementId: string;
  /** Which catalog category the element comes from — used by the drop handler
   *  to decide whether to instantly select for inline edit (text) or just
   *  place (shape/icon/badge). */
  category: ElementCategoryId;
};
